import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Form, Select, DatePicker, Space } from 'antd';
import {
  ECellValueType,
  ExcelTableColDef,
  EExcelTablePaginationType,
} from 'components/ui/excel-table/types';
import { useMemo, useEffect, useState, useCallback } from 'react';
import ExcelTable from 'components/ui/excel-table';
import ExportV2Icon from 'assets/icons/export-v2';
import usePagination from 'hooks/usePagination';
import { useQuery } from 'react-query';
import { receiptApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import { RowClickedEvent } from 'ag-grid-community';
import { ENavigationPath } from 'constants/path';
import { CashReceiptRow } from './types';
import { formatReceiptNumber } from 'utils/format';
import FilterButton from 'components/ui/filter-button';
import { SortedSelect } from 'components/ui/sorted-select';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const CashReceipts: React.FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  // Watch form values for filtering
  const selectedSourceBrokers = Form.useWatch('sourceBrokers', form);
  const selectedBankAccounts = Form.useWatch('bankAccounts', form);
  const dateRange = Form.useWatch('dateRange', form);
  const selectedCurrencies = Form.useWatch('currencies', form);

  // Reset pagination when filters change
  useEffect(() => {
    resetPagination();
  }, [
    selectedSourceBrokers,
    selectedBankAccounts,
    dateRange,
    selectedCurrencies,
    resetPagination,
  ]);

  const { data: receipts, isLoading: isReceiptsLoading } = useQuery({
    queryKey: [
      EQueryKeys.LIST_RECEIPTS,
      currentPage,
      pageSize,
      selectedSourceBrokers,
      selectedBankAccounts,
      dateRange,
      selectedCurrencies,
    ],
    queryFn: () =>
      receiptApi.filterReceipts({
        page: currentPage,
        pageSize: pageSize,
        receiptFilterQueryDto: {
          sourceBrokerIds: selectedSourceBrokers?.length
            ? selectedSourceBrokers
            : undefined,
          bankAccountNumbers: selectedBankAccounts?.length
            ? selectedBankAccounts
            : undefined,
          currencies: selectedCurrencies?.length
            ? selectedCurrencies
            : undefined,
          transactionDateFrom: dateRange?.[0]?.startOf('day').toDate(),
          transactionDateTo: dateRange?.[1]?.endOf('day').toDate(),
          includeSourceBroker: true,
          includeBankAccount: true,
          includeFiles: true,
        },
      }),
  });

  const { data: sourceBrokers, isLoading: isSourceBrokersLoading } = useQuery({
    queryKey: [EQueryKeys.LIST_RECEIPT_SOURCE_BROKERS],
    queryFn: () => receiptApi.getReceiptSourceBrokers(),
  });

  const { data: bankAccounts, isLoading: isBankAccountsLoading } = useQuery({
    queryKey: [EQueryKeys.LIST_RECEIPT_BANK_ACCOUNTS],
    queryFn: () => receiptApi.getReceiptBankAccounts(),
  });

  const { data: currencies, isLoading: isCurrenciesLoading } = useQuery({
    queryKey: [EQueryKeys.LIST_RECEIPT_CURRENCIES],
    queryFn: () => receiptApi.getReceiptCurrencies(),
  });

  const sourceBrokerOptions = useMemo(() => {
    return (
      sourceBrokers?.map((broker) => ({
        label: broker.name,
        value: broker.brokerId,
      })) || []
    );
  }, [sourceBrokers]);

  const bankAccountOptions = useMemo(() => {
    return (
      bankAccounts?.map((account) => ({
        label: `${account.bankName} (${account.currencyCode})`,
        value: account.bankNumber,
      })) || []
    );
  }, [bankAccounts]);

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        field: 'receiptNumber',
        headerName: t('cashReceipts.receiptNumber'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'transactionDate',
        headerName: t('cashReceipts.transactionDate'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'bank',
        headerName: t('cashReceipts.bank'),
        minWidth: 300,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'currency',
        headerName: t('cashReceipts.currency'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'sourceBroker',
        headerName: t('cashReceipts.sourceBroker'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'brokerReference',
        headerName: t('cashReceipts.brokerReference'),
        minWidth: 150,
        flex: 1,
        resizable: true,
        editable: false,
      },
      {
        field: 'receiptAmount',
        headerName: t('cashReceipts.receiptAmount'),
        minWidth: 150,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },
      {
        field: 'receiptBalanceOutstanding',
        headerName: t('cashReceipts.receiptBalanceOutstanding'),
        minWidth: 180,
        flex: 1,
        type: ECellValueType.NUMERIC_COLUMN,
        resizable: true,
        editable: false,
      },
    ],
    [t]
  );

  const rowData: CashReceiptRow[] = useMemo(() => {
    return (
      receipts?.items.map((item, index) => ({
        receiptNumber: formatReceiptNumber(item.id),
        transactionDate: dayjs(item.transactionDate).format('DD/MM/YYYY'),
        bank: item.bankAccount.accountName,
        currency: item.bankAccount.currencyCode,
        sourceBroker: item.sourceBroker?.name,
        brokerReference: item.sourceBrokerReference,
        receiptAmount: Number(item.amount),
        receiptBalanceOutstanding: Number(item.remainingAmount),
        id: pageSize * (currentPage - 1) + index + 1,
        _id: item.id,
      })) || []
    );
  }, [currentPage, pageSize, receipts?.items]);

  const handleRowClick = useCallback(
    (row: RowClickedEvent) => {
      navigate(ENavigationPath.UPDATE_RECEIPT.replace(':id', row.data._id));
    },
    [navigate]
  );

  return (
    <div className="rounded-lg bg-white px-3 py-4">
      <Form form={form} className="w-full">
        <div className="flex items-center justify-between mb-6">
          <Title level={4} className="ml-4">
            {t('receipts.cashReceipts')}
          </Title>

          <div className="flex gap-2">
            <FilterButton
              isActive={isFilterOpen}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            />
            <button className="flex items-center gap-2 px-6 py-2 shadow-md bg-[#1d67b6] text-white rounded-lg hover:bg-[#1d67b6]/90 transition-colors">
              <ExportV2Icon />
              {t('general.export')}
            </button>
          </div>
        </div>
        {isFilterOpen && (
          <Space direction="horizontal">
            <Form.Item name="currencies" className="min-w-[220px]">
              <SortedSelect
                mode="multiple"
                placeholder={t('cashReceipts.selectCurrencies')}
                options={currencies?.map((currency) => ({
                  label: currency,
                  value: currency,
                }))}
                loading={isCurrenciesLoading}
                size="large"
                maxTagCount={2}
                className="rounded-xl"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  ((option?.label ?? '') as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item name="dateRange" className="w-[280px]">
              <RangePicker
                format="DD/MM/YYYY"
                size="large"
                placeholder={[t('general.startDate'), t('general.endDate')]}
                allowClear
                disabledDate={(current) =>
                  current && current > dayjs().endOf('day')
                }
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item name="sourceBrokers" className="min-w-[250px]">
              <SortedSelect
                mode="multiple"
                placeholder={t('cashReceipts.selectSourceBrokers')}
                options={sourceBrokerOptions}
                loading={isSourceBrokersLoading}
                allowClear
                maxTagCount={1}
                showSearch
                size="large"
                className="rounded-xl"
                filterOption={(input, option) =>
                  ((option?.label ?? '') as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item name="bankAccounts" className="min-w-[250px]">
              <SortedSelect
                mode="multiple"
                placeholder={t('cashReceipts.selectBanks')}
                options={bankAccountOptions}
                loading={isBankAccountsLoading}
                size="large"
                maxTagCount={1}
                className="rounded-xl"
                allowClear
                showSearch
                filterOption={(input, option) =>
                  ((option?.label ?? '') as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Space>
        )}
      </Form>

      <ExcelTable
        data={rowData}
        columnDefs={columnDefs}
        onRowClicked={handleRowClick}
        paginationType={EExcelTablePaginationType.DETAIL}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        pageSize={pageSize}
        currentPage={currentPage}
        totalRows={receipts?.pagination?.total || 0}
        tableContainerClassName="h-[28rem]"
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={true}
        loading={isReceiptsLoading}
      />
    </div>
  );
};

export default CashReceipts;
